import FbButton from 'components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { Anchor } from 'components/UI/Anchor'
import { cn } from 'components/UI/cn'
import { Select } from 'components/UI/Select/Select'
import { Switch } from 'components/UI/Switch/Switch'
import {
  ChainSearchResult,
  useDoorsAndChainsSearch,
} from 'features/elastic/searchers'
import { PiX as XIcon } from 'react-icons/pi'
import { useState, useMemo, useCallback } from 'react'
import { useDebounce } from 'use-debounce'
import { useUnifiedSearchCompanyUrl } from 'utils/getUnifiedSearchCompanyUrl'
import { CSVRow, ImportData, InternalColumns } from '../importer-types'
import { useImporterViewModel, getColumnOption } from '../ImporterViewModel'
import {
  setMatchedChainByHash,
  setAsCustomCompany,
} from '../importDataTransforms'
import { OppTypeBadge, getOppTypeLabel } from './OppTypeBadge'

/** Fields to display in the "Your Input" column - company_match and primary_key types */
type MappedField = {
  mapped: string
  original: string
  label: string
}

function clearCustomCompanyByHash(
  data: ImportData,
  companyHash: string
): ImportData {
  return {
    ...data,
    rows: data.rows.map((row) =>
      row[InternalColumns.COMPANY_HASH] === companyHash
        ? {
            ...row,
            [InternalColumns.IS_CUSTOM_COMPANY]: false,
            [InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]: null,
            [InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]: null,
          }
        : row
    ),
  }
}

export function ImportWizardReviewPendingStep({
  show,
  data,
}: {
  show: boolean
  data: CSVRow[]
}) {
  const viewModel = useImporterViewModel()
  const [cursor, setCursor] = useState(0)

  // Derived, clamped cursor: always within [0, data.length - 1] (or 0 when empty).
  // Replaces the old useEffect that re-synced `cursor` when `data.length` shrank.
  const safeCursor = useMemo(() => {
    if (data.length === 0) return 0
    return Math.min(cursor, data.length - 1)
  }, [cursor, data.length])

  function handleNext() {
    if (safeCursor + 1 >= data.length) {
      return
    }
    setCursor(safeCursor + 1)
  }

  function handlePrev() {
    if (safeCursor - 1 < 0) {
      return
    }
    setCursor(safeCursor - 1)
  }

  // Handler to clear selection for current company
  const handleClearSelection = useCallback(() => {
    const hash = data[safeCursor]?.[InternalColumns.COMPANY_HASH]
    if (!hash) return

    viewModel.updateImportData((d) => {
      const result = setMatchedChainByHash(d, hash, null, false)
      return clearCustomCompanyByHash(result, hash)
    })
  }, [safeCursor, data, viewModel])

  // Build dropdown options for company types
  const companyTypeOptions = useMemo(() => {
    return (
      viewModel.companySubcategories?.map((subcat) => ({
        label: `${subcat.category_name} / ${subcat.subcategory_name}`,
        value: `${subcat.category_id}-${subcat.value}`,
      })) ?? []
    )
  }, [viewModel.companySubcategories])

  // Get current row's action status
  const currentRow = data[safeCursor]
  const currentRowHash = currentRow?.[InternalColumns.COMPANY_HASH]
  const isCurrentRowCustom = currentRow?.[InternalColumns.IS_CUSTOM_COMPANY]
  const currentRowCategoryId =
    currentRow?.[InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]
  const currentRowSubcategoryId =
    currentRow?.[InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]

  // Compute the selected company type value for the dropdown
  const selectedCompanyType = useMemo(() => {
    if (
      isCurrentRowCustom &&
      currentRowCategoryId != null &&
      currentRowSubcategoryId != null
    ) {
      return `${currentRowCategoryId}-${currentRowSubcategoryId}`
    }
    return null
  }, [isCurrentRowCustom, currentRowCategoryId, currentRowSubcategoryId])

  // No auto-default — users must explicitly pick a type before creating a custom company.
  const effectiveCompanyType = selectedCompanyType

  // Handler to toggle current company as custom.
  // `value` is a "category-subcategory" string when setting, or null when
  // clearing the custom flag (toggle off).
  const handleSetCustom = useCallback(
    (value: string | null) => {
      if (!currentRowHash) return

      if (value === null) {
        viewModel.updateImportData((d) =>
          clearCustomCompanyByHash(d, currentRowHash)
        )
        return
      }

      const [categoryId, subcategoryId] = value.split('-').map(Number)
      viewModel.updateImportData((d) => {
        let result = setAsCustomCompany(
          d,
          currentRowHash,
          categoryId,
          subcategoryId
        )
        result = setMatchedChainByHash(result, currentRowHash, null, false)
        return result
      })
    },
    [currentRowHash, viewModel]
  )

  if (!show) {
    return null
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No pending records to review
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 h-full">
      {/* Progress header */}
      <div className="flex items-center justify-between border-b pb-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="text-lg font-bold text-gray-700 tabular-nums">
            {safeCursor + 1}
            <span className="text-gray-400"> / {data.length}</span>
          </div>
          <div className="h-1.5 w-32 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-fb-dark-green transition-all duration-300"
              style={{ width: `${((safeCursor + 1) / data.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <FbButton
            type="button"
            onClick={handleClearSelection}
            variant="secondary"
          >
            Clear
          </FbButton>
          <div className="flex gap-1.5">
            <FbButton
              type="button"
              onClick={handlePrev}
              disabled={safeCursor === 0}
            >
              ← Prev
            </FbButton>
            <FbButton
              type="button"
              onClick={handleNext}
              disabled={safeCursor === data.length - 1}
            >
              Next →
            </FbButton>
          </div>
        </div>
      </div>

      {/* Review card */}
      <div className="flex-1 min-h-0">
        <ReviewCard
          key={data[safeCursor]?.[InternalColumns.COMPANY_HASH]}
          csvRow={data[safeCursor]}
          companyTypeOptions={companyTypeOptions}
          selectedCompanyType={effectiveCompanyType}
          isCustomSelected={!!isCurrentRowCustom}
          onSetCustom={handleSetCustom}
        />
      </div>
    </div>
  )
}

/**
 * Stable identity for a chain result across search-vs-suggestion sources.
 * Compares on `type + item_id` and stringifies to avoid accidental
 * number/string mismatches between the two pipelines.
 */
function chainIdentity(c: ChainSearchResult | null | undefined): string {
  if (!c) return ''
  return `${c.type}::${String(c.item_id)}`
}

/**
 * Best-effort coercion of a user-facing domain string (e.g. "mcdonalds.com",
 * "www.foo.com", "https://foo.com/path") into a safe external URL.
 */
function toExternalUrl(domain: string): string {
  const trimmed = domain.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

function ReviewCard({
  csvRow,
  companyTypeOptions,
  selectedCompanyType,
  isCustomSelected,
  onSetCustom,
}: {
  csvRow: CSVRow
  companyTypeOptions: Array<{ label: string; value: string }>
  selectedCompanyType: string | null
  isCustomSelected: boolean
  onSetCustom: (value: string | null) => void
}) {
  const viewModel = useImporterViewModel()

  // Read current selection directly from the row (single source of truth)
  const currentSelectedChain = csvRow[InternalColumns.MATCHED_CHAIN]
  const companyHash = csvRow[InternalColumns.COMPANY_HASH]

  const suggestions = useMemo(
    () => csvRow.__internal__suggested_chains ?? [],
    [csvRow.__internal__suggested_chains]
  )

  const handleSelectChain = useCallback(
    (chain: ChainSearchResult) => {
      viewModel.updateImportData((d) => {
        const result = setMatchedChainByHash(d, companyHash, chain, false)
        return clearCustomCompanyByHash(result, companyHash)
      })
    },
    [companyHash, viewModel]
  )

  const handleDeselectChain = useCallback(() => {
    viewModel.updateImportData((d) =>
      setMatchedChainByHash(d, companyHash, null, false)
    )
  }, [companyHash, viewModel])

  // Get all mapped company fields (company_match and primary_key types)
  const mappedCompanyFields = useMemo((): MappedField[] => {
    return (
      viewModel.importData?.headers
        .filter((h) => {
          const option = getColumnOption(h.mapped)
          return (
            option?.type === 'company_match' || option?.type === 'primary_key'
          )
        })
        .map((h) => ({
          mapped: h.mapped,
          original: h.original,
          label: getColumnOption(h.mapped)?.name || h.mapped,
        })) || []
    )
  }, [viewModel.importData?.headers])

  // Build input fields dynamically from mapped headers
  const inputFields = useMemo(() => {
    return mappedCompanyFields.map((field) => ({
      label: field.label,
      value: String(csvRow[field.original] ?? '') || '-',
    }))
  }, [mappedCompanyFields, csvRow])

  return (
    <div className="flex flex-row gap-6 h-full min-h-0">
      {/* Left: Comparison Table — 40% for slimmer read column */}
      <div className="basis-2/5 flex-shrink-0 overflow-auto">
        <ComparisonSummary
          inputFields={inputFields}
          selectedChain={currentSelectedChain}
          isChainManualMatch={csvRow[InternalColumns.IS_CHAIN_MANUAL_MATCH]}
        />
      </div>

      {/* Right: Match panel — 60% for denser interactive content */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <ChainMatchPanel
          suggestions={suggestions}
          selectedChain={currentSelectedChain}
          onSelect={handleSelectChain}
          onDeselect={handleDeselectChain}
          dimmed={isCustomSelected}
        />

        {/* OR divider — stronger mode boundary */}
        <div
          className="flex items-center gap-3 my-4 flex-shrink-0"
          role="separator"
          aria-label="or"
        >
          <div className="flex-1 h-px bg-line" />
          <span className="text-xs font-semibold text-fg-secondary tracking-widest uppercase">
            Or
          </span>
          <div className="flex-1 h-px bg-line" />
        </div>

        {/* Section B: Create custom */}
        <div
          className={cn(
            'flex-shrink-0 transition-opacity',
            !!currentSelectedChain && 'opacity-60'
          )}
        >
          <CreateCustomCompanyCard
            companyTypeOptions={companyTypeOptions}
            selectedCompanyType={selectedCompanyType}
            isSelected={isCustomSelected}
            onSelect={onSetCustom}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * Chain match panel: owns search inputs, suggestion/result list derivation,
 * number-key hotkeys, and the pinned "currently selected" row when the
 * selected chain is not visible in the current list.
 *
 * Invariants:
 * - Mode flip (suggestions vs search) uses *immediate* input values so
 *   clearing an input returns to suggestions without waiting for debounce.
 * - Only the network request is debounced.
 * - The selected chain is always visible when one exists — either inside
 *   the list (highlighted) or pinned above the list.
 * - Hotkeys 1-5 address whatever is currently visible in order, with the
 *   pinned selected row (if present) taking slot 1.
 */
function ChainMatchPanel({
  suggestions,
  selectedChain,
  onSelect,
  onDeselect,
  dimmed,
}: {
  suggestions: ChainSearchResult[]
  selectedChain: ChainSearchResult | null
  onSelect: (chain: ChainSearchResult) => void
  onDeselect: () => void
  dimmed: boolean
}) {
  const [nameQuery, setNameQuery] = useState('')
  const [domainQuery, setDomainQuery] = useState('')

  // Mode switch uses *immediate* values so clearing inputs returns to
  // suggestions instantly. The network request is still debounced below.
  const searchActive = nameQuery.trim() !== '' || domainQuery.trim() !== ''

  const [debouncedName] = useDebounce(nameQuery.trim(), 400)
  const [debouncedDomain] = useDebounce(domainQuery.trim(), 400)

  const { data: searchResults, isLoading: isSearching } =
    useDoorsAndChainsSearch(
      searchActive
        ? { name: debouncedName, domain: debouncedDomain }
        : { name: '', domain: '' }
    )

  const listedChains = useMemo<ChainSearchResult[]>(() => {
    const source = searchActive
      ? (searchResults?.chains ?? [])
      : [...suggestions].sort((a, b) => b.score - a.score)

    // Dedupe by item_id — the combined search can surface the same chain
    // from two pipelines (direct chain hit + door→chain rollup) and does
    // not always dedupe across them. item_id alone is the entity key;
    // `type` may differ across pipelines for the same chain.
    const seen = new Set<string>()
    const unique: ChainSearchResult[] = []
    for (const chain of source) {
      const id = String(chain.item_id ?? '')
      if (id && !seen.has(id)) {
        seen.add(id)
        unique.push(chain)
      }
    }

    return unique.slice(0, searchActive ? 10 : 5)
  }, [searchActive, searchResults, suggestions])

  const selectedId = chainIdentity(selectedChain)
  const selectedInList = useMemo(
    () => listedChains.some((c) => chainIdentity(c) === selectedId),
    [listedChains, selectedId]
  )
  const showPinnedSelected = !!selectedChain && !selectedInList

  const handleToggleChain = useCallback(
    (chain: ChainSearchResult) => {
      if (chainIdentity(chain) === selectedId) {
        onDeselect()
      } else {
        onSelect(chain)
      }
    },
    [selectedId, onSelect, onDeselect]
  )

  const placeholderSlots = !searchActive
    ? Math.max(0, 5 - listedChains.length - (showPinnedSelected ? 1 : 0))
    : 0

  const countLabel = searchActive
    ? `Results (${listedChains.length})`
    : `Suggestions (${Math.min(suggestions.length, 5)})`

  return (
    <div
      className={cn(
        'flex flex-col gap-2 min-h-0 flex-1 transition-opacity',
        dimmed && 'opacity-60'
      )}
    >
      <div>
        <div className="text-sm font-semibold text-fg">
          Match to an existing chain
        </div>
        <div className="text-xs text-fg-secondary">
          Pick a suggestion below, or search by name or domain to find more.
        </div>
      </div>

      <div className="flex items-center gap-2">
        <ClearableSearchInput
          value={nameQuery}
          onChange={setNameQuery}
          placeholder="Search by name..."
          ariaLabel="Search chains by name"
          clearAriaLabel="Clear name"
        />
        <ClearableSearchInput
          value={domainQuery}
          onChange={setDomainQuery}
          placeholder="Search by domain..."
          ariaLabel="Search chains by domain"
          clearAriaLabel="Clear domain"
        />
      </div>

      <div className="text-xs font-medium text-fg-secondary">{countLabel}</div>

      {/* Column header — pinned above the scrolling list */}
      <ChainListHeader />

      {/* Single scroll container — pinned selected (if any), list, placeholders */}
      <div className="flex-1 overflow-auto min-h-0 space-y-1">
        {showPinnedSelected && selectedChain && (
          <SuggestionCard
            key={`pinned-${chainIdentity(selectedChain)}`}
            data={selectedChain}
            isSelected
            onSelect={() => handleToggleChain(selectedChain)}
          />
        )}

        {searchActive && isSearching && (
          <div className="flex items-center justify-center p-4 text-sm text-fg-secondary">
            Searching…
          </div>
        )}

        {searchActive && !isSearching && listedChains.length === 0 && (
          <div className="flex items-center justify-center p-4 text-sm text-fg-secondary">
            No chains match
          </div>
        )}

        {listedChains.map((chain, i) => {
          const id = chainIdentity(chain)
          return (
            <SuggestionCard
              key={id || `row-${i}`}
              data={chain}
              isSelected={id === selectedId}
              onSelect={() => handleToggleChain(chain)}
            />
          )
        })}

        {placeholderSlots > 0 &&
          Array.from({ length: placeholderSlots }).map((_, i) => (
            <PlaceholderCard key={`placeholder-${i}`} />
          ))}
      </div>
    </div>
  )
}

function ChainListHeader() {
  return (
    <div className="flex items-center gap-2 px-2 py-1 text-xs font-medium text-fg-secondary border-b bg-surface flex-shrink-0">
      <div className="flex-shrink-0 w-4"></div>
      <div className="flex-1 grid grid-cols-4 gap-2">
        <div>Name</div>
        <div>Domain</div>
        <div>Cities</div>
        <div>States</div>
      </div>
    </div>
  )
}

function PlaceholderCard() {
  return (
    <div className="invisible" aria-hidden>
      <SuggestionCard
        data={{
          item_id: '',
          name: '-',
          domain: '-',
          count: 0,
          type: '',
          score: 0,
        }}
        isSelected={false}
        onSelect={() => {}}
      />
    </div>
  )
}

function CreateCustomCompanyCard({
  companyTypeOptions,
  selectedCompanyType,
  isSelected,
  onSelect,
}: {
  companyTypeOptions: Array<{ label: string; value: string }>
  selectedCompanyType: string | null
  isSelected: boolean
  onSelect: (value: string | null) => void
}) {
  // `pickerValue` is initialized from `selectedCompanyType` at mount, falling
  // back to the first available option so the toggle is always actionable.
  // Row navigation triggers a remount via `key={...COMPANY_HASH}` on the
  // parent `ReviewCard`, which naturally resets this state. Do not add a
  // useEffect to re-sync on prop changes — that would fight the remount.
  const [pickerValue, setPickerValue] = useState<string | null>(
    selectedCompanyType ?? companyTypeOptions[0]?.value ?? null
  )

  const handleToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        if (!pickerValue) return
        onSelect(pickerValue)
      } else {
        onSelect(null)
      }
    },
    [pickerValue, onSelect]
  )

  return (
    <div
      className={cn(
        'w-full border rounded px-4 py-3 flex flex-col gap-2 transition-all',
        isSelected
          ? 'border-green-500 bg-green-50'
          : 'border-gray-200 bg-white opacity-60'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-fg">
          Create as custom company
        </span>
        <Switch
          checked={isSelected}
          onChange={handleToggle}
          disabled={!pickerValue}
          aria-label="Create as custom company"
        />
      </div>

      <div className="text-xs text-fg-secondary">
        Custom company won&apos;t match any existing chain.
      </div>

      <Select
        className="w-full"
        items={companyTypeOptions}
        selected={pickerValue}
        onSelect={(v) => {
          // Guard: never allow nulling the picker — keep the current value
          // if the Select emits null (e.g. clicking the already-selected row).
          if (!v) return
          setPickerValue(v)
          if (isSelected) {
            onSelect(v)
          }
        }}
        buttonSize="sm"
      />
    </div>
  )
}

function SuggestionCard({
  data,
  isSelected,
  onSelect,
}: {
  data: ChainSearchResult
  isSelected: boolean
  onSelect: () => void
}) {
  const { getCompanyUrlFromId } = useUnifiedSearchCompanyUrl()
  const chainId = `${data.type}${data.item_id}`

  // Build company URL for the name link
  const companyUrl = getCompanyUrlFromId(chainId)

  return (
    <div
      className={cn(
        'border rounded px-2 py-0.5 cursor-pointer transition-all',
        isSelected
          ? 'border-green-500 bg-green-50'
          : 'border-gray-200 bg-white hover:border-gray-400'
      )}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2">
        <div
          className={cn('text-green-600 font-semibold whitespace-nowrap', {
            invisible: !isSelected,
          })}
        >
          ✓
        </div>
        <div className="flex-1 min-w-0">
          <div className={`grid grid-cols-4 gap-2 text-sm`}>
            <div className="font-medium text-gray-900 truncate">
              {companyUrl ? (
                <FbLink
                  to={companyUrl}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                >
                  {data.name || '-'}
                </FbLink>
              ) : (
                data.name || '-'
              )}
            </div>
            <div className="truncate">
              {data.domain ? (
                <Anchor
                  href={toExternalUrl(data.domain)}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={(e) => e.stopPropagation()}
                  title={data.domain}
                >
                  {data.domain}
                </Anchor>
              ) : (
                <span className="text-gray-700">-</span>
              )}
            </div>
            {'city_summary' in data && (
              <div
                className="text-fg truncate"
                title={data.city_summary || '-'}
              >
                {data.city_summary || '-'}
              </div>
            )}
            {'state_summary' in data && (
              <div
                className="text-fg truncate"
                title={data.state_summary || '-'}
              >
                {data.state_summary || '-'}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-xxs text-gray-400">
            <span>Chain: {chainId}</span>
            <OppTypeBadge typeCode={data.type} size="sm" />
          </div>
        </div>
      </div>
    </div>
  )
}

function ComparisonSummary({
  inputFields,
  selectedChain,
  isChainManualMatch,
}: {
  inputFields: Array<{ label: string; value: string }>
  selectedChain?: ChainSearchResult | null
  isChainManualMatch?: boolean
}) {
  const { getCompanyUrlFromId } = useUnifiedSearchCompanyUrl()
  const hasChain = !!selectedChain

  // Build URL for selected chain
  const chainUrl = selectedChain
    ? getCompanyUrlFromId(`${selectedChain.type}${selectedChain.item_id}`)
    : null

  // Map field labels to chain property names
  const fieldToChainKey: Record<string, string> = {
    'company name': 'name',
    'company domain': 'domain',
    'company city': 'city',
    'company state': 'state',
    'company country': 'country',
    'company type': 'type',
    'company full address': 'address',
    'company street': 'address',
    'company zip': 'zipcode',
  }

  // Get chain value for a field label
  const getChainValue = (fieldLabel: string): string => {
    if (!selectedChain) return '-'
    const key = fieldToChainKey[fieldLabel.toLowerCase()]
    if (!key) return '-'
    switch (key) {
      case 'name':
        return selectedChain.name || '-'
      case 'type':
        return getOppTypeLabel(selectedChain.type) || '-'
      case 'domain':
        return selectedChain.domain || '-'
      case 'city':
        return selectedChain.city_summary || '-'
      case 'state':
        return selectedChain.state_summary || '-'
      default:
        return '-'
    }
  }

  // Check if field is a "name" field for linking
  const isNameField = (fieldLabel: string): boolean => {
    return fieldToChainKey[fieldLabel.toLowerCase()] === 'name'
  }

  return (
    <div className="overflow-hidden rounded border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-2 py-1.5 text-left font-semibold text-gray-700 whitespace-nowrap">
              Field
            </th>
            <th className="px-2 py-1.5 text-left font-semibold text-fb-dark-green border-l border-gray-200 whitespace-nowrap">
              Your Input
            </th>
            <th className="px-2 py-1.5 text-left font-semibold border-l border-gray-200 whitespace-nowrap">
              <span className={hasChain ? 'text-green-700' : 'text-gray-400'}>
                Selected Chain
              </span>
              {isChainManualMatch && (
                <span className="ml-1.5 text-xs px-1 py-0.5 bg-blue-100 text-blue-700 rounded">
                  Manual
                </span>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {inputFields.map((field, index) => (
            <tr
              key={field.label}
              className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
            >
              <td className="px-2 py-1.5 font-medium text-gray-600 border-b border-gray-100 whitespace-nowrap">
                {field.label}
              </td>
              <td className="px-2 py-1.5 text-gray-900 border-l border-gray-200 border-b border-gray-100">
                {field.value || '-'}
              </td>
              <td className="px-2 py-1.5 border-l border-gray-200 border-b border-gray-100">
                {isNameField(field.label) && chainUrl ? (
                  <FbLink to={chainUrl} target="_blank">
                    {getChainValue(field.label)}
                  </FbLink>
                ) : (
                  <span className="text-gray-900">
                    {getChainValue(field.label)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Single-line search input with an inline clear (X) button and Escape-to-clear.
 * Used for the name and domain inline search fields.
 */
function ClearableSearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  clearAriaLabel,
}: {
  value: string
  onChange: (next: string) => void
  placeholder: string
  ariaLabel: string
  clearAriaLabel: string
}) {
  return (
    <div className="relative flex-1">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="w-full px-2 py-1 text-sm border border-line rounded bg-white focus:outline-none focus:ring-1 focus:ring-brand"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onChange('')
            ;(e.target as HTMLInputElement).blur()
          }
        }}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-fg-secondary hover:text-fg"
          aria-label={clearAriaLabel}
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}
